import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api as http } from "@/api";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Upload } from "lucide-react";

const LABELS = {
  party_name: "Party Name", page: "Page No.", conference: "Conference Name", group: "Group Name",
  item: "Item", shade: "Shade", qty: "Qty", mtr: "MTR", bill_no: "Bill Number", rate: "Rate",
};

// Import an Excel/CSV file into the Conference Order sheet, with column mapping.
export const ExcelImportDialog = ({ open, onOpenChange, onImported }) => {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState("append");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const picked = useRef(null);

  const reset = () => {
    setPreview(null);
    setMapping({});
    picked.current = null;
  };

  const choose = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    picked.current = file;
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      const { data } = await http.post("/conf/order-rows/import/preview", body);
      setPreview(data);
      setMapping(data.mapping || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not read that file");
      reset();
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    if (!picked.current) return;
    const body = new FormData();
    body.append("file", picked.current);
    body.append("mapping", JSON.stringify(mapping));
    body.append("mode", mode);
    setBusy(true);
    try {
      const { data } = await http.post("/conf/order-rows/import", body);
      toast.success(`${data.imported} row(s) imported${data.skipped ? `, ${data.skipped} blank row(s) skipped` : ""}`);
      onImported?.();
      onOpenChange(false);
      reset();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const template = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/conf/order-rows/template`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="excel-import-dialog" className="max-w-3xl rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-lg font-black uppercase tracking-tight">Import Excel</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Pick an .xlsx or .csv file. Columns are matched by their heading — change any that look wrong.
            You can also just copy cells in Excel and press Ctrl+V straight onto the sheet.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={choose} className="hidden"
            data-testid="excel-file-input" />
          <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="excel-pick-btn"
            onClick={() => fileRef.current?.click()} disabled={busy}>
            <FileSpreadsheet className="mr-1 h-3.5 w-3.5" /> Choose file
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="excel-template-btn"
            onClick={template}>
            <Download className="mr-1 h-3.5 w-3.5" /> Template
          </Button>
          {preview && (
            <span className="text-[11px] text-muted-foreground" data-testid="excel-file-info">
              {picked.current?.name} · {preview.total_rows} row(s)
            </span>
          )}
        </div>

        {preview && (
          <>
            <div className="max-h-[300px] overflow-auto border border-[color:var(--sheet-border)]">
              <table className="w-full text-[11px]" data-testid="excel-preview-table">
                <thead>
                  <tr className="bg-[color:var(--sheet-head2)]">
                    {preview.headers.map((h, i) => (
                      <th key={i} className="border border-[color:var(--sheet-border)] p-1 text-left align-top">
                        <p className="truncate font-bold uppercase">{h || `Column ${i + 1}`}</p>
                        <select
                          data-testid={`excel-map-${i}`}
                          value={mapping[String(i)] || ""}
                          onChange={(e) => {
                            const next = { ...mapping };
                            if (e.target.value) next[String(i)] = e.target.value;
                            else delete next[String(i)];
                            setMapping(next);
                          }}
                          className="mt-1 h-6 w-full border border-[color:var(--sheet-border)] bg-white px-1 text-[10px]"
                        >
                          <option value="">— skip —</option>
                          {preview.fields.map((f) => (
                            <option key={f} value={f}>{LABELS[f] || f}</option>
                          ))}
                        </select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, ri) => (
                    <tr key={ri}>
                      {preview.headers.map((_, ci) => (
                        <td key={ci} className="border border-[color:var(--sheet-border)] px-1 py-0.5">
                          {r[ci] ?? ""}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-[11px]">
                {[["append", "Add to the sheet"], ["replace", "Replace everything"]].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5">
                    <input type="radio" data-testid={`excel-mode-${k}`} checked={mode === k}
                      onChange={() => setMode(k)} />
                    {label}
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-[11px]"
                  data-testid="excel-cancel-btn" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button size="sm" className="h-8 text-[11px]" disabled={busy}
                  data-testid="excel-import-btn" onClick={run}>
                  <Upload className="mr-1 h-3.5 w-3.5" /> {busy ? "Importing…" : "Import rows"}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
